import React, {useEffect, useState} from 'react';

import {useStore} from 'effector-react';
import {saveAs} from 'file-saver';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

import {exportReturnAuditEvent, fetchReturnAuditEvent} from 'api/returnAudit';
import {ReactComponent as DownloadIcon} from 'assets/svg/download.svg';
import {AuditGeneralInformation} from 'components/common/AuditLog/GeneralInformation';
import {
    DetailCard,
    DetailHeader,
    DetailHeaderActions,
    DetailId,
    DetailTitle,
    EmptyDetail,
    EventBadge,
    Field,
    FieldLabel,
    FieldsGrid,
    FieldValue,
    Section,
    SectionTitle,
} from 'components/common/AuditLog/styled';
import {BackTo} from 'components/common/BackTo';
import {Page} from 'components/common/Page';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {StatusWrapper} from 'components/ui-kit/PageComponents';
import {Spinner} from 'components/ui-kit/Spinner';
import {PERMISSION_ALIASES} from 'core/constants/permissionAliases';
import {DOCUMENT_STATUS_COLORS, PRODUCT_STATUS_COLORS} from 'core/constants/statuses';
import {ProductStatus} from 'core/dictionaries';
import {RestError, showRestError} from 'core/errors';
import {ROUTE} from 'core/routes';
import {permissions$} from 'models/auth';
import {ReturnAuditEntry, ReturnAuditEventType} from 'types/return-audit';

const eventFields: Record<ReturnAuditEventType, Array<keyof ReturnAuditEntry>> = {
    RETURN_DOCUMENT_AUTO_CREATED: [
        'documentId',
        'markingCode',
        'organizationId',
        'sourceFormation',
        'documentStatus',
    ],
    RETURN_RECEIPT_REJECTED: ['markingCode', 'organizationId', 'rejectionReason'],
    RETURN_DOCUMENT_MANUAL_CREATED: [
        'documentId',
        'markingCode',
        'organizationId',
        'sourceFormation',
        'productCondition',
        'disposalReason',
        'storageLocationName',
        'comment',
    ],
    RETURN_DOCUMENT_SUBMITTED: ['documentId', 'markingCode', 'storageLocationName'],
    RETURN_DOCUMENT_PROCESSING: [
        'documentId',
        'markingCode',
        'documentStatus',
        'storageLocationName',
    ],
    RETURN_DOCUMENT_COMPLETED_REINTRODUCED: [
        'documentId',
        'markingCode',
        'storageLocationName',
        'previousStatus',
    ],
    RETURN_DOCUMENT_COMPLETED_WRITTEN_OFF: [
        'documentId',
        'markingCode',
        'disposalReason',
        'previousStatus',
    ],
    RETURN_DOCUMENT_REJECTED: ['documentId', 'rejectionReason'],
};

export const ReturnAuditDetailPage = () => {
    const {id} = useParams<{id: string}>();
    const {t} = useTranslation();
    const permissions = useStore(permissions$);
    const [entry, setEntry] = useState<ReturnAuditEntry | null>(null);
    const [pending, setPending] = useState(true);
    const [exportPending, setExportPending] = useState(false);
    useEffect(() => {
        let active = true;
        setEntry(null);
        setPending(true);
        fetchReturnAuditEvent(id)
            .then((value) => {
                if (active) setEntry(value);
            })
            .catch((error) => {
                if (active) showRestError(error as RestError);
            })
            .finally(() => {
                if (active) setPending(false);
            });
        return () => {
            active = false;
        };
    }, [id]);
    const exportPdf = async () => {
        if (exportPending) return;
        setExportPending(true);
        try {
            saveAs(await exportReturnAuditEvent(id), `return-audit-${id}.pdf`);
        } catch (error) {
            showRestError(error as RestError);
        } finally {
            setExportPending(false);
        }
    };
    const productStatus = (value: string) => (
        <StatusWrapper
            color={PRODUCT_STATUS_COLORS[value as ProductStatus] || PRODUCT_STATUS_COLORS.DEFAULT}
        >
            {t(`dictionaries.products.status.${value}`, {defaultValue: value})}
        </StatusWrapper>
    );
    const renderValue = (key: keyof ReturnAuditEntry): React.ReactNode => {
        const value = entry?.[key];
        if (value == null || value === '') return '—';
        if (key === 'sourceFormation')
            return t(`pages.returnAudit.sources.${value}`, {defaultValue: String(value)});
        if (key === 'productCondition')
            return t(`dictionaries.documents.returnGoods.productCondition.${value}`, {
                defaultValue: String(value),
            });
        if (key === 'disposalReason')
            return t(`dictionaries.documents.returnGoods.reasons.${value}`, {
                defaultValue: String(value),
            });
        if (key === 'previousStatus') return productStatus(String(value));
        if (key === 'documentStatus') {
            const status = String(value);
            const color = Object.prototype.hasOwnProperty.call(DOCUMENT_STATUS_COLORS, status)
                ? DOCUMENT_STATUS_COLORS[status as keyof typeof DOCUMENT_STATUS_COLORS]
                : DOCUMENT_STATUS_COLORS.DEFAULT;
            return (
                <StatusWrapper color={color}>
                    {t(`dictionaries.documents.status.${value}`, {defaultValue: String(value)})}
                </StatusWrapper>
            );
        }
        return value;
    };
    const newStatus =
        entry?.eventType === 'RETURN_DOCUMENT_COMPLETED_REINTRODUCED'
            ? 'INTRODUCED'
            : entry?.eventType === 'RETURN_DOCUMENT_COMPLETED_WRITTEN_OFF'
            ? 'DROPOUT'
            : undefined;
    return (
        <Page title={t('pages.recallAudit.detail.title')}>
            <BackTo to={ROUTE.RETURN_AUDIT}>{t('pages.recallAudit.detail.back')}</BackTo>
            {pending ? (
                <EmptyDetail>
                    <Spinner />
                </EmptyDetail>
            ) : !entry ? (
                <EmptyDetail>{t('pages.recallAudit.detail.notFound')}</EmptyDetail>
            ) : (
                <DetailCard>
                    <DetailHeader>
                        <div>
                            <DetailTitle>{t('pages.recallAudit.detail.title')}</DetailTitle>
                            <DetailId>ID: {entry.eventId}</DetailId>
                        </div>
                        <DetailHeaderActions>
                            <EventBadge>
                                {t(`pages.returnAudit.events.${entry.eventType}`, {
                                    defaultValue: entry.eventType,
                                })}
                            </EventBadge>
                            {permissions.includes(PERMISSION_ALIASES.returnAuditLogExport) && (
                                <Button
                                    size="sm"
                                    variant={ButtonVariant.Secondary}
                                    disabled={exportPending}
                                    isLoading={exportPending}
                                    onClick={exportPdf}
                                    icon={<Icon source={DownloadIcon} width={16} height={16} />}
                                >
                                    {t('pages.recallAudit.export.pdf')}
                                </Button>
                            )}
                        </DetailHeaderActions>
                    </DetailHeader>
                    <AuditGeneralInformation entry={entry} />
                    <Section>
                        <SectionTitle>{t('pages.returnAudit.details')}</SectionTitle>
                        <FieldsGrid>
                            {(eventFields[entry.eventType] || [])
                                .filter(
                                    (key) =>
                                        !['comment', 'storageLocationName'].includes(key) ||
                                        entry[key] != null,
                                )
                                .map((key) => (
                                    <Field key={key} wide={key === 'comment'}>
                                        <FieldLabel>
                                            {t(`pages.returnAudit.fields.${key}`)}
                                        </FieldLabel>
                                        <FieldValue>{renderValue(key)}</FieldValue>
                                    </Field>
                                ))}
                            {newStatus && (
                                <Field>
                                    <FieldLabel>
                                        {t('pages.returnAudit.fields.newStatus')}
                                    </FieldLabel>
                                    <FieldValue>{productStatus(newStatus)}</FieldValue>
                                </Field>
                            )}
                        </FieldsGrid>
                    </Section>
                </DetailCard>
            )}
        </Page>
    );
};
